
// Top-level composition of the Lennar Roadmap app.
//
// State is split across two hooks (lib/hooks.ts):
//   useDataLayer — initiatives + roster (localStorage in Phase 1, API in Phase 2)
//   useRoadmapView — view/scope/filters/active drawer + filtered initiatives
//
// AppShell wires them together with side effects (toasts).

import * as React from "react";
import { toast } from "sonner";
import { TEAMS } from "@/lib/constants";
import { useDataLayer, useRoadmapView } from "@/lib/hooks";
import { Icon } from "./icons";
import { Timeline } from "./timeline";
import { ListView } from "./list-view";
import { WeeklyReview } from "./weekly-review";
import { Drawer } from "./drawer";
import { NewInitiativeModal } from "./new-initiative-modal";
import { FilterBar } from "./filter-bar";
import { ExportMenu } from "./export-menu";
import { UserMenu } from "./user-menu";
import { WhatsNewTour, hasSeenTour } from "./whats-new-tour";
import { AUTH_ENABLED } from "@/lib/config";
import { renewSignIn } from "@/lib/auth";
import { GROUP_LEVELS, GROUP_LEVEL_LABELS } from "@/lib/group-by";
import { inTimelineWindow, timelineWindowLabel } from "@/lib/timeline-window";

export function AppShell() {
  const data = useDataLayer();
  const ui = useRoadmapView(data.initiatives);

  const searchRef = React.useRef<HTMLInputElement | null>(null);

  // What's new tour: plays once (per browser) after the roadmap loads, on
  // the timeline where its steps live; the What's new button replays it.
  // Skipped on phone-width screens, which open on the List view.
  const [tourOpen, setTourOpen] = React.useState(false);
  const tourChecked = React.useRef(false);
  const { setView } = ui;
  const startTour = React.useCallback(() => {
    setView("timeline");
    // Let the timeline render before the tour measures its targets.
    window.setTimeout(() => setTourOpen(true), 300);
  }, [setView]);
  React.useEffect(() => {
    // Wait for the data: a failed load shows the error screen, not the tour.
    if (!data.hydrated || data.loadError || tourChecked.current) return;
    tourChecked.current = true;
    if (!hasSeenTour() && window.innerWidth >= 768) startTour();
  }, [data.hydrated, data.loadError, startTour]);

  // The timeline draws only what overlaps its window, so the header says
  // "6 of 10 … in Sep 2026 – Aug 2027" when some matches are off-screen.
  const windowCount = React.useMemo(
    () => inTimelineWindow(ui.filtered, ui.windowStart, ui.filters.quarter).length,
    [ui.filtered, ui.windowStart, ui.filters.quarter],
  );
  const showWindowCount = ui.view === "timeline" && windowCount < ui.filtered.length;

  // "/" focuses search from anywhere outside a form field.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement;
      if (t.closest("input, textarea, select, [contenteditable]")) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);


  return (
    <>
      {/* Skip-to-content link for keyboard users */}
      <a href="#main" className="skip-to-content">Skip to content</a>

      {/* Top nav */}
      <header className="topnav">
        <div className="brand">
          <span>Lennar</span>
          <span className="brand-divider" />
          <span className="brand-sub">Digital Experience</span>
        </div>
        <div className="topnav-actions">
          <button type="button" className="btn sm ghost" onClick={startTour} data-tour="whats-new">
            What's new
          </button>
          <UserMenu />
        </div>
      </header>

      <main className="page-shell" id="main">
        <div className="page-eyebrow">Digital Roadmap</div>
        <div className="page-meta" style={{ marginTop: 6 }} data-tour="count">
          {showWindowCount ? (
            <span title="The timeline shows initiatives that overlap this window. Step with ‹ › or use the List view to see the rest.">
              <span style={{ fontWeight: 500, color: "var(--ink)" }}>{windowCount}</span> of{" "}
              <span style={{ fontWeight: 500, color: "var(--ink)" }}>{ui.filtered.length}</span> initiatives in {timelineWindowLabel(ui.windowStart, ui.filters.quarter)}
            </span>
          ) : (
            <span><span style={{ fontWeight: 500, color: "var(--ink)" }}>{ui.filtered.length}</span> initiatives</span>
          )}
          <span className="dot" />
          <span><span style={{ fontWeight: 500, color: "var(--ink)" }}>{TEAMS.length}</span> teams</span>
          {!data.hydrated && (
            <>
              <span className="dot" />
              <span style={{ fontFamily: "var(--font-source-serif), serif", fontStyle: "italic" }}>Loading…</span>
            </>
          )}
        </div>

        <div className="toolbar">
          {/* Timeline / List toggle */}
          <div className="seg">
            <button type="button" className={ui.view === "timeline" ? "active" : ""} onClick={() => ui.setView("timeline")}>
              <Icon name="timeline" size={12} /> Timeline
            </button>
            <button type="button" className={ui.view === "list" ? "active" : ""} onClick={() => ui.setView("list")}>
              <Icon name="list" size={12} /> List
            </button>
            <button type="button" className={ui.view === "review" ? "active" : ""} onClick={() => ui.setView("review")}>
              <Icon name="calendar" size={12} /> Review
            </button>
          </div>

          {/* Group by Team > Pod > Workstream (any combination; none = flat) —
              only meaningful on the timeline. See lib/group-by.ts. */}
          {ui.view === "timeline" && (
            <div className="seg group-by" style={{ marginLeft: 8 }} role="group" aria-label="Group the timeline by" data-tour="group-by">
              <span className="group-by-label">Group by</span>
              {GROUP_LEVELS.map(level => (
                <label key={level} className={ui.scope.groupBy[level] ? "active" : ""}>
                  <input
                    type="checkbox"
                    checked={ui.scope.groupBy[level]}
                    onChange={() => ui.setGroupBy({ ...ui.scope.groupBy, [level]: !ui.scope.groupBy[level] })}
                  />
                  {GROUP_LEVEL_LABELS[level]}
                </label>
              ))}
            </div>
          )}

          <div className="search-box">
            <Icon name="search" size={12} />
            <input
              ref={searchRef}
              type="search"
              value={ui.query}
              onChange={e => ui.setQuery(e.target.value)}
              placeholder="Search initiatives…"
              aria-label="Search initiatives by name"
            />
            {ui.query && (
              <button type="button" className="search-clear" aria-label="Clear search" onClick={() => ui.setQuery("")}>
                <Icon name="x" size={10} />
              </button>
            )}
          </div>

          <FilterBar filters={ui.filters} setFilters={ui.setFilters} roster={data.roster} initiatives={data.initiatives} currentUserId={data.currentUser?.id ?? null} />

          <ExportMenu
            all={data.initiatives}
            filtered={ui.filtered}
            roster={data.roster}
            filters={ui.filters}
            query={ui.query}
            timelineWindowStart={ui.view === "timeline" ? ui.windowStart : null}
          />

          <div className="toolbar-spacer" />

          <button type="button" className="btn primary" onClick={ui.openNew} data-tour="new">
            <Icon name="plus" size={13} /> New initiative
          </button>
        </div>

        {!data.hydrated ? (
          <div className="empty-shell"><p>Loading…</p></div>
        ) : data.loadError ? (
          // Not the empty state: the data exists, we just couldn't reach it.
          <div className="empty-shell" role="alert" style={{ marginTop: 32 }}>
            <h3>Couldn't load the roadmap.</h3>
            <p>
              {AUTH_ENABLED
                ? "Your sign-in may have expired, or the roadmap service can't be reached right now."
                : "The roadmap service can't be reached right now."}
            </p>
            <p className="load-error-detail">{data.loadError}</p>
            <div style={{ marginTop: 20, display: "flex", gap: 8, justifyContent: "center" }}>
              <button type="button" className="btn primary" onClick={data.reload}>Try again</button>
              {AUTH_ENABLED && (
                <button type="button" className="btn" onClick={renewSignIn}>Sign in again</button>
              )}
            </div>
          </div>
        ) : data.initiatives.length === 0 ? (
          <div className="empty-shell" style={{ marginTop: 32 }}>
            <h3>The roadmap is empty.</h3>
            <p>Add your first initiative to start populating the roadmap.</p>
            <div style={{ marginTop: 20 }}>
              <button type="button" className="btn primary" onClick={ui.openNew}>
                <Icon name="plus" size={13} /> New initiative
              </button>
            </div>
          </div>
        ) : ui.view === "timeline" ? (
          <Timeline
            initiatives={ui.filtered}
            scope={ui.scope}
            windowStart={ui.windowStart}
            onWindowStartChange={ui.setWindowStart}
            /* Quarters picked in the filter = the view zooms to exactly
               those quarters (one, or a run of adjacent ones). */
            zoomQuarters={ui.filters.quarter}
            onZoomQuarter={(qId) => ui.setFilters({ ...ui.filters, quarter: [qId] })}
            onExitZoom={() => ui.setFilters({ ...ui.filters, quarter: [] })}
            onBarClick={ui.openDrawer}
            activeId={ui.activeId}
            onUpdate={data.update}
            roster={data.roster}
            allInitiatives={data.initiatives}
            onLinkDependency={(from, to) => {
              const previousId = to.dependsOnId;
              const previous = previousId ? data.initiatives.find(i => i.id === previousId) : undefined;
              data.update(to.id, { dependsOnId: from.id });
              toast(`${to.name} now depends on ${from.name}`, {
                description: previous ? `Replaced: depends on ${previous.name}` : undefined,
                action: { label: "Undo", onClick: () => data.update(to.id, { dependsOnId: previousId }) },
                duration: 8000,
              });
            }}
            onRemoveDependency={(dependent) => {
              const previousId = dependent.dependsOnId;
              const previous = previousId ? data.initiatives.find(i => i.id === previousId) : undefined;
              data.update(dependent.id, { dependsOnId: undefined });
              toast(`${dependent.name} no longer depends on ${previous?.name ?? "anything"}`, {
                action: { label: "Undo", onClick: () => data.update(dependent.id, { dependsOnId: previousId }) },
                duration: 8000,
              });
            }}
            onMoveWorkstream={(i, workstream) => {
              const previous = i.workstream;
              data.update(i.id, { workstream });
              toast(`Moved ${i.name} to ${workstream ?? "no workstream"}`, {
                action: { label: "Undo", onClick: () => data.update(i.id, { workstream: previous }) },
                duration: 8000,
              });
            }}
          />
        ) : ui.view === "review" ? (
          <WeeklyReview
            initiatives={ui.filtered}
            roster={data.roster}
            onOpen={ui.openDrawer}
          />
        ) : (
          <ListView
            initiatives={ui.filtered}
            roster={data.roster}
            onRowClick={ui.openDrawer}
            showPod={ui.filters.pod.length > 0}
          />
        )}
      </main>

      <Drawer
        initiative={ui.activeInitiative}
        initiatives={data.initiatives}
        onOpen={ui.openDrawer}
        roster={data.roster}
        currentUser={data.currentUser}
        onClose={ui.closeDrawer}
        onUpdate={(id, patch) => {
          data.update(id, patch);
          toast.success("Saved");
        }}
        onArchive={(id) => {
          const archived = data.initiatives.find(i => i.id === id);
          data.archive(id);
          // Archive is one click with no confirm step, so this toast is the
          // safety net: say what happened and give real time to Undo.
          toast(`Archived ${archived?.name ?? "initiative"}`, {
            description: "It's hidden from the roadmap.",
            action: {
              label: "Undo",
              onClick: () => {
                data.restore(id);
                toast.success("Restored");
              },
            },
            duration: 10000,
          });
        }}
        onPostUpdate={(id, payload) => {
          const u = data.postUpdate(id, payload);
          toast.success("Update posted");
          return u;
        }}
        onPickPerson={data.addPerson}
      />

      <WhatsNewTour open={tourOpen} onClose={() => setTourOpen(false)} />

      <NewInitiativeModal
        open={ui.showNew}
        onClose={ui.closeNew}
        onCreate={(payload) => {
          const inst = data.create(payload);
          ui.closeNew();
          toast.success(`Created · ${inst.name}`);
          setTimeout(() => ui.setActiveId(inst.id), 200);
        }}
        roster={data.roster}
        onPickPerson={data.addPerson}
        currentUser={data.currentUser}
        initiatives={data.initiatives}
      />
    </>
  );
}
